/**
 * 认证状态管理
 * 管理用户登录状态、Token、权限等
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../services/apiClient';
import { API_ENDPOINTS } from '../services/endpoints';
import type { User, LoginRequest } from '../types/auth';
import { isSystemRole } from '../config/featurePermissions';

const clearAvatarAuthAttemptFlags = () => {
  if (typeof window === 'undefined') return;
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith('hhcd_avatar_auth_attempted_'))
    .forEach((key) => sessionStorage.removeItem(key));
};

// 登录回调、路由守卫可能在同一次渲染周期内同时恢复用户信息。
// 只合并仍在进行中的请求；请求结束后的显式刷新仍会重新访问服务端。
let loadUserRequest: {
  id: symbol;
  token: string | null;
  promise: Promise<void>;
} | null = null;

interface AuthState {
  // 状态
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;

  // 操作
  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      loading: false,

      // 登录
      login: async (data: LoginRequest) => {
        set({ loading: true });
        try {
          const response = await apiClient.post(
            API_ENDPOINTS.AUTH.LOGIN,
            data
          ) as any;
          // 后端返回统一格式 { code, data: { access_token, refresh_token, user }, message }
          const loginData = response.data?.data || response.data;
          const { access_token, refresh_token, user } = loginData;
          // 登录成功清除手动登出标记
          sessionStorage.removeItem('manual_logout');
          sessionStorage.removeItem('manual_logout_at');
          set({
            token: access_token,
            refreshToken: refresh_token,
            user,
            isAuthenticated: true,
            loading: false,
          });
        } catch (error) {
          set({ loading: false });
          throw error;
        }
      },

      // 退出登录
      logout: async () => {
        try {
          await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
        } catch {
          // 退出登录即使失败也要清除本地状态
        } finally {
          // 标记手动退出，防止企微内置浏览器瞬间又自动登录
          sessionStorage.setItem('manual_logout_at', String(Date.now()));
          clearAvatarAuthAttemptFlags();
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
          });
        }
      },

      // 加载用户信息
      loadUser: () => {
        const requestToken = get().token;
        if (loadUserRequest?.token === requestToken) {
          return loadUserRequest.promise;
        }

        const requestId = Symbol('loadUserRequest');
        const request = {
          id: requestId,
          token: requestToken,
          promise: Promise.resolve(),
        };
        // Register the request before starting it so even a synchronous client
        // failure cannot leave a completed request cached as still in flight.
        loadUserRequest = request;

        const requestPromise = (async () => {
          try {
            const response = await apiClient.get(API_ENDPOINTS.AUTH.ME, {
              suppressErrorToast: true,
            } as any) as any;
            // A logout or another login may have happened while this request was
            // in flight. Never let an old profile restore a different session.
            if (get().token !== requestToken) {
              return;
            }
            // 后端返回统一格式 { code, data: user, message }
            const user = response.data?.data || response.data;
            set({ user, isAuthenticated: true });
          } catch (error) {
            console.error('[loadUser] Failed to fetch user profile:', error);
            // 仅在本地当前没有任何 Token 的前提下才重置状态，绝不能因为单次拉取个人信息失败/超时而抹去已持有的登录态导致循环重定向！
            const currentToken = get().token;
            if (currentToken === requestToken && !currentToken) {
              set({
                token: null,
                refreshToken: null,
                user: null,
                isAuthenticated: false,
              });
            }
          } finally {
            if (loadUserRequest?.id === requestId) {
              loadUserRequest = null;
            }
          }
        })();

        request.promise = requestPromise;
        return requestPromise;
      },

      // 检查权限
      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        if (isSystemRole(user)) return true;
        if (user.is_superuser) return true;
        if (user.permissions?.includes('*')) return true;
        return user.permissions?.includes(permission) ?? false;
      },

      // 设置加载状态
      setLoading: (loading: boolean) => set({ loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// 保持全局 token 引用同步，解决 localStorage 写入延迟导致的 401 Race Condition 自动登出/循环登录问题
if (typeof window !== 'undefined') {
  (window as any).__wecom_token__ = useAuthStore.getState().token;
  (window as any).__wecom_refresh_token__ = useAuthStore.getState().refreshToken;
  
  useAuthStore.subscribe((state) => {
    (window as any).__wecom_token__ = state.token;
    (window as any).__wecom_refresh_token__ = state.refreshToken;
  });

  // 在应用初始化时，从 localStorage 中同步恢复全局变量引用，确保首个请求直接带上 token
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage);
      const token = parsed?.state?.token;
      const refreshToken = parsed?.state?.refreshToken;
      if (token) {
        (window as any).__wecom_token__ = token;
      }
      if (refreshToken) {
        (window as any).__wecom_refresh_token__ = refreshToken;
      }
    } catch (e) {
      // ignore
    }
  }
}
